import type {
  LiveUpdatesSection,
  LiveUpdatesStack,
  LiveUpdatesStatus,
  LiveUpdatesStatusKey,
} from 'api/liveUpdates.types'

// FR-75 delivery lock modes, derived purely from the stack's delivery
// provider: 'self_managed' (Thanx never receives delivery signals) and
// 'disabled' (delivery isn't an enabled handoff mode for these locations).
export type LiveUpdatesLockedMode = 'self_managed' | 'disabled'

// The two sections whose statuses reach Thanx ONLY as Olo webhooks. Until the
// merchant connects those webhooks neither has a signal to configure, so each
// swaps its cards for an actionable connect-webhooks card. The value names
// which section's copy the card shows.
export type LiveUpdatesWebhookGate = 'delivery' | 'canceled'

// One rendered section of the Live Updates config page. `titleKey`/`noteKey`
// are translation keys relative to the `live_updates` root; `statuses` are the
// cards to render under the header, already filtered for applicability.
// `suppressedStatuses` (FR-75) is the ordered list of statuses the locked or
// webhook-gated card lists as unavailable — empty for every other section.
export interface LiveUpdatesSectionPlan {
  sectionKey: LiveUpdatesSection
  titleKey: string
  noteKey?: string
  lockedMode?: LiveUpdatesLockedMode
  webhookGate?: LiveUpdatesWebhookGate
  suppressedStatuses: LiveUpdatesStatusKey[]
  statuses: LiveUpdatesStatus[]
}

// Provider-key sentinels the delivery provider can carry to signal a locked
// (non-configurable) delivery integration (FR-75).
const DELIVERY_SELF_MANAGED_KEY = 'self_managed'
const DELIVERY_DISABLED_KEY = 'disabled'

// FR-75: the delivery statuses a locked delivery card lists as suppressed, in
// render order. `canceled_by_delivery` lives in the `canceled` section but is
// surfaced here (and excluded from the Canceled-orders section) when delivery
// is locked.
const DELIVERY_SUPPRESSED_STATUSES: LiveUpdatesStatusKey[] = [
  'picked_up',
  'en_route',
  'arrived',
  'delivered',
  'canceled_by_delivery',
]

// Ordered section blueprint: which merchant-facing section each wire `section`
// value maps to, its header/note translation keys, and the render order
// (FR-33). Note keys live in different namespaces — the shared-stages note is
// top-level (`shared_note`), the ready note nests under `sections`.
const SECTION_BLUEPRINT: {
  sectionKey: LiveUpdatesSection
  titleKey: string
  noteKey?: string
}[] = [
  {
    sectionKey: 'shared',
    titleKey: 'sections.placed_preparation',
    noteKey: 'shared_note',
  },
  {
    sectionKey: 'pickup',
    titleKey: 'sections.ready',
    noteKey: 'sections.ready_note',
  },
  { sectionKey: 'delivery', titleKey: 'sections.delivery' },
  { sectionKey: 'canceled', titleKey: 'sections.canceled' },
]

// FR-75: the delivery section's locked treatment, derived purely from the
// stack's delivery provider + whether delivery is a configured handoff mode.
// Returns undefined for a normally-configurable delivery integration.
// Exported for the Overview tab's connected-events card (FR-37), which needs
// the same "is delivery actually trackable" answer to decide whether a
// Delivery row belongs in its status summary at all.
export const deliveryLockedMode = (
  stack: LiveUpdatesStack,
  deliveryConfigurable: boolean
): LiveUpdatesLockedMode | undefined => {
  const key = stack.deliveryProvider?.key
  if (key === DELIVERY_SELF_MANAGED_KEY) return 'self_managed'
  if (key === DELIVERY_DISABLED_KEY) return 'disabled'
  if (!deliveryConfigurable) return 'disabled'
  return undefined
}

// Which sections a missing Olo webhook connection gates. A locked delivery
// integration wins over the gate: connecting webhooks would not turn those
// updates on, so its own lock card (which explains the real reason) stays.
const webhookGateFor = (
  sectionKey: LiveUpdatesSection,
  deliveryLocked: boolean
): LiveUpdatesWebhookGate | undefined => {
  if (sectionKey === 'delivery') return deliveryLocked ? undefined : 'delivery'
  if (sectionKey === 'canceled') return 'canceled'
  return undefined
}

// Groups the flat status list into the ordered page render plan. Pure — the
// single source of truth for section membership, header/note copy, delivery
// lock mode, and per-status applicability filtering.
//
// Rules:
// - Non-delivery sections drop inapplicable statuses (e.g. a pickup-only
//   group's canceled_by_delivery, whose `applicable` is false, never renders).
// - The delivery section is omitted entirely when the stack neither has a
//   delivery provider nor is delivery configurable (e.g. a Flybuy stack,
//   which never populates `delivery_provider` but can still be
//   `delivery_configurable: true`). Whenever either signal is present the
//   section renders every delivery status regardless of `applicable` — that
//   gate, not the per-status flag, decides section membership, and a locked
//   provider still needs its (disabled) cards shown.
// - An otherwise-empty section is omitted.
// - `oloWebhooksGated` gates the delivery and canceled sections behind a
//   connect-webhooks card: those statuses only ever arrive as Olo webhooks, so
//   while the connection is not yet delivering events (not_connected OR
//   waiting) there is nothing to configure yet.
export function sectionForStatuses(
  statuses: LiveUpdatesStatus[],
  stack: LiveUpdatesStack,
  deliveryConfigurable: boolean,
  oloWebhooksGated = false
): LiveUpdatesSectionPlan[] {
  const showDeliverySection =
    stack.deliveryProvider !== null || deliveryConfigurable

  // Resolve the delivery lock once — the canceled section keys off it too
  // (defense-in-depth: a locked delivery integration hides its
  // canceled_by_delivery card and surfaces it in the suppressed list instead).
  const lockedMode = showDeliverySection
    ? deliveryLockedMode(stack, deliveryConfigurable)
    : undefined
  const deliveryLocked = lockedMode !== undefined

  return SECTION_BLUEPRINT.reduce<LiveUpdatesSectionPlan[]>(
    (plan, blueprint) => {
      const isDeliverySection = blueprint.sectionKey === 'delivery'
      const isCanceledSection = blueprint.sectionKey === 'canceled'

      // Omit the delivery section outright when the stack can't deliver.
      if (isDeliverySection && !showDeliverySection) return plan

      const sectionStatuses = statuses.filter(status => {
        if (status.section !== blueprint.sectionKey) return false
        // The section-level gate above already decided the delivery section
        // applies — keep every delivery status so locked/disabled cards still
        // render.
        if (isDeliverySection) return true
        // When delivery is locked, the canceled_by_delivery card moves into the
        // delivery suppressed list — drop it from Canceled orders here on top of
        // the `applicable` filter below.
        if (
          isCanceledSection &&
          deliveryLocked &&
          status.status === 'canceled_by_delivery'
        ) {
          return false
        }
        return status.applicable
      })

      if (sectionStatuses.length === 0) return plan

      const lockedDelivery = isDeliverySection && deliveryLocked
      const webhookGate = oloWebhooksGated
        ? webhookGateFor(blueprint.sectionKey, deliveryLocked)
        : undefined

      // A gated section lists exactly the statuses it would have rendered as
      // cards, so each message is named under one header only. The lock takes
      // the fixed list because it also drops canceled_by_delivery from the
      // canceled section (above), leaving its card that status's only home.
      let suppressedStatuses: LiveUpdatesStatusKey[] = []
      if (lockedDelivery) {
        suppressedStatuses = [...DELIVERY_SUPPRESSED_STATUSES]
      } else if (webhookGate) {
        suppressedStatuses = sectionStatuses.map(status => status.status)
      }

      plan.push({
        sectionKey: blueprint.sectionKey,
        titleKey: blueprint.titleKey,
        noteKey: blueprint.noteKey,
        lockedMode: isDeliverySection ? lockedMode : undefined,
        webhookGate,
        suppressedStatuses,
        statuses: sectionStatuses,
      })

      return plan
    },
    []
  )
}
